import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useLenis } from 'lenis/react';
import { EASE_IN_OUT } from '../../lib/motion';
import { useScrollTo } from './SmoothScroll';

const COVER = 0.55;
const REVEAL = 0.65;

// Two stacked panels sweep up over the old page (a brand sliver leading an ink panel),
// the route swaps underneath, then both continue upward to uncover the new page.
const Curtain = ({ title, enter }) => {
  const panel = (delay, className, children) => (
    <motion.div
      aria-hidden="true"
      className={`pointer-events-none fixed inset-0 z-[80] ${className}`}
      initial={{ scaleY: enter ? 1 : 0 }}
      animate={{ scaleY: 0, transition: { duration: REVEAL, ease: EASE_IN_OUT, delay } }}
      exit={{ scaleY: 1, transition: { duration: COVER, ease: EASE_IN_OUT, delay: 0.08 - delay } }}
      style={{ originY: enter ? 0 : 1 }}
    >
      {children}
    </motion.div>
  );

  return (
    <>
      {panel(0.08, 'bg-gradient-to-r from-brand to-brand-2')}
      {panel(0, 'flex items-center justify-center bg-raised', <span className="label text-foreground/70">{title}</span>)}
    </>
  );
};

const HASH_WAIT = 8000;

/*
 * Mounted with each page: jump to the top, then to the #hash target, while the curtain covers.
 * This sits outside the routes' Suspense, so a lazy page may not have rendered yet: wait
 * frame by frame for the target to exist. Lenis re-measures first, because its size updates
 * are debounced and may predate the new page, which would cut the jump short.
 */
const ScrollOnMount = ({ hash }) => {
  const scrollTo = useScrollTo();
  const lenis = useLenis();
  useEffect(() => {
    scrollTo(0, { immediate: true });
    const id = hash && decodeURIComponent(hash.slice(1));
    if (!id) return undefined;
    const started = performance.now();
    let frame = 0;
    const seek = () => {
      const target = document.getElementById(id);
      if (target) {
        lenis?.resize();
        scrollTo(target, { immediate: true });
      } else if (performance.now() - started < HASH_WAIT) {
        frame = requestAnimationFrame(seek);
      }
    };
    frame = requestAnimationFrame(seek);
    return () => cancelAnimationFrame(frame);
  }, []);
  return null;
};

/*
 * Animated route changes. The curtain covers the old page, the new page mounts and resets
 * its scroll position underneath, then the curtain uncovers it. The first page load skips
 * the curtain so the hero's own intro plays straight away. Hash changes within a page
 * (e.g. /salesforce → /salesforce#testimonials) just glide to the section.
 */
const RouteTransition = ({ children, titleFor }) => {
  const location = useLocation();
  const reduced = useReducedMotion();
  const scrollTo = useScrollTo();
  const first = useRef(true);
  const lastPath = useRef(location.pathname);

  useEffect(() => {
    first.current = false;
  }, []);

  useEffect(() => {
    if (lastPath.current !== location.pathname) {
      lastPath.current = location.pathname;
      return;
    }
    const target = location.hash && document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (target) scrollTo(target, { immediate: reduced });
  }, [location.pathname, location.hash, location.key, scrollTo, reduced]);

  const page = (
    <>
      <ScrollOnMount hash={location.hash} />
      {children(location)}
    </>
  );

  if (reduced) return <React.Fragment key={location.pathname}>{page}</React.Fragment>;

  return (
    <AnimatePresence mode="wait">
      <motion.div key={location.pathname} className="relative">
        {page}
        <Curtain title={titleFor(location.pathname)} enter={!first.current} />
      </motion.div>
    </AnimatePresence>
  );
};

export default RouteTransition;
